// layoutGraph.js
//
// "Clean up layout" engine — columnar 5×5 auto-layout.
// See devdocs/designer_layout_and_appearance_spec.md.
//
// The survey flow is arranged into columns read top-to-bottom, left-to-right:
//   - at most 5 nodes per column, at most 5 columns per block;
//   - when a block's five columns are full, the next block continues BELOW
//     the first, with one full spacing row between blocks;
//   - vertical spacing inside a column uses each node's MEASURED height
//     (React Flow's width/height), falling back to per-type estimates only
//     for nodes that haven't rendered yet;
//   - at a decision, the first branch continues in the current (or next)
//     column; each further branch starts after an EMPTY spacer column, and
//     the post-merge continuation also resumes after a spacer column. The
//     empty gutters make each branch path easy to trace and click;
//   - where branches rejoin, the merge node resumes in the column after the
//     spacer that follows the rightmost branch.
// Space efficiency is explicitly not the goal; readability is.
// Only node positions change; nothing else about the graph is touched.

// Fallback sizes when React Flow hasn't measured a node yet.
const FALLBACK_SIZE = {
  question: { w: 250, h: 120 },
  media: { w: 250, h: 120 },
  package: { w: 250, h: 140 },
  decision: { w: 180, h: 180 },
  start: { w: 80, h: 80 },
  end: { w: 80, h: 80 },
};

// Slot geometry is derived from the question node, per spec (rev. 2026-08-02):
// row gap = 0.5 × question height, column gap = 0.7 × question width.
// Widened from the original 0.22 × H / 0.3 × W after live use on a large
// survey (Rand Health) showed the tight gutters left no room for edge labels
// and forced parallel edges onto the same channel. The gutters now fit a
// label chip (~200 px max) and several spread-apart edge runs.
const Q = FALLBACK_SIZE.question;
const ROW_GAP = Math.round(0.5 * Q.h);           // 60
const COL_GAP = Math.round(0.7 * Q.w);           // 175
const MAX_ROWS = 5;                              // nodes per column
const COLS_PER_BLOCK = 5;                        // columns per block
const BLOCK_GAP = Q.h + 2 * ROW_GAP;             // horizontal edge channel between blocks
const MARGIN = 40;

// Exported so tests (and any future tuning UI) read the real geometry instead
// of mirroring magic numbers.
export const LAYOUT_GEOMETRY = {
  QUESTION_W: Q.w,
  QUESTION_H: Q.h,
  ROW_GAP,
  COL_GAP,
  MAX_ROWS,
  COLS_PER_BLOCK,
  BLOCK_GAP,
  MARGIN,
};

const nodeHeight = (n) => n.height || (FALLBACK_SIZE[n.type] || { h: 100 }).h;
const nodeWidth = (n) => n.width || (FALLBACK_SIZE[n.type] || { w: 220 }).w;

/**
 * Compute cleaned-up positions for the whole graph.
 *
 * @param {Array} nodes  React Flow nodes
 * @param {Array} edges  React Flow edges
 * @param {Object} opts  kept for signature compatibility
 * @returns {Array} new node array with updated positions
 */
export function layoutGraph(nodes = [], edges = [], _opts = {}) {
  if (!nodes.length) return nodes;

  const ids = new Set(nodes.map(n => String(n.id)));
  const nodeById = new Map(nodes.map(n => [String(n.id), n]));

  // Adjacency (edges array order preserved — decision branches keep their
  // declared order) and in-degree, ignoring self-loops and dangling edges.
  const out = new Map();
  const inDeg = new Map();
  const preds = new Map();
  for (const e of edges) {
    const s = String(e.source);
    const t = String(e.target);
    if (!ids.has(s) || !ids.has(t) || s === t) continue;
    if (!out.has(s)) out.set(s, []);
    out.get(s).push(t);
    inDeg.set(t, (inDeg.get(t) || 0) + 1);
    if (!preds.has(t)) preds.set(t, []);
    preds.get(t).push(s);
  }

  // Placement state.
  const placed = new Map();    // id -> { col, row }
  const colNodes = new Map();  // col -> [id, ...] in row order
  const reserved = new Set();  // spacer columns kept empty on purpose
  let maxCol = 0;

  /** Place a node in the first non-reserved column ≥ col with a free row. */
  function placeNode(id, col) {
    let c = Math.max(0, col);
    while (reserved.has(c) || (colNodes.get(c) || []).length >= MAX_ROWS) c += 1;
    const list = colNodes.get(c) || [];
    placed.set(id, { col: c, row: list.length });
    list.push(id);
    colNodes.set(c, list);
    if (c > maxCol) maxCol = c;
    return c;
  }

  /**
   * Walk a chain from `id`, placing nodes column-major from column `col`.
   * Inside a branch (isBranch), stop at any merge node (in-degree > 1) and
   * hand it back to the decision that spawned the branches — the merge is
   * placed by the decision loop, past a spacer column, never inline in a
   * branch. Back edges (targets already placed) end the walk.
   */
  function walk(id, col, isBranch) {
    let node = id;
    let curCol = col;
    while (node) {
      if (placed.has(node)) return { col: curCol, deferred: null };
      if (isBranch && (inDeg.get(node) || 0) > 1) {
        return { col: curCol, deferred: node };
      }
      curCol = placeNode(node, curCol);
      const succs = out.get(node) || [];
      if (succs.length === 0) return { col: curCol, deferred: null };
      if (succs.length === 1) { node = succs[0]; continue; }

      // Decision (or any multi-out node): the first branch continues here;
      // each further branch gets an empty spacer column, then its own column.
      const ends = [];
      const deferred = [];
      for (let i = 0; i < succs.length; i++) {
        let startCol = curCol;
        if (i > 0) {
          reserved.add(maxCol + 1); // spacer after the previous branch run
          startCol = maxCol + 2;
        }
        const res = walk(succs[i], startCol, true);
        ends.push(res.col);
        if (res.deferred) deferred.push(res.deferred);
      }
      curCol = Math.max(...ends);
      const merge = deferred.find(d => !placed.has(d));
      if (!merge) return { col: curCol, deferred: null };
      // Continuation after the branches also resumes past a spacer column.
      reserved.add(maxCol + 1);
      curCol = maxCol + 2;
      node = merge;
    }
    return { col: curCol, deferred: null };
  }

  // Start at the start node (fall back to any node without inbound edges,
  // then to the first node).
  const start = nodes.find(n => n.type === 'start')
    || nodes.find(n => !(inDeg.get(String(n.id)) > 0))
    || nodes[0];
  walk(String(start.id), 0, false);

  // Anything unreached (disconnected fragments) continues in following slots.
  for (const n of nodes) {
    const id = String(n.id);
    if (!placed.has(id)) walk(id, maxCol, false);
  }

  // ---- Geometry pass ----
  // Vertical spacing uses measured node heights, so rows in different
  // columns don't necessarily align; each column stacks its own contents.

  // y offset of each node within its column, and each column's total height.
  const colOffsets = new Map(); // col -> [yOffset per row]
  const colHeights = new Map(); // col -> total content height
  for (const [col, list] of colNodes.entries()) {
    let y = 0;
    const offsets = [];
    for (const id of list) {
      offsets.push(y);
      y += nodeHeight(nodeById.get(id)) + ROW_GAP;
    }
    colOffsets.set(col, offsets);
    colHeights.set(col, Math.max(0, y - ROW_GAP));
  }

  // Block tops: each block is as tall as its tallest column.
  const blockCount = Math.floor(maxCol / COLS_PER_BLOCK) + 1;
  const blockTops = [];
  let top = MARGIN;
  for (let b = 0; b < blockCount; b++) {
    blockTops.push(top);
    let tallest = 0;
    for (let c = b * COLS_PER_BLOCK; c < (b + 1) * COLS_PER_BLOCK; c++) {
      tallest = Math.max(tallest, colHeights.get(c) || 0);
    }
    top += tallest + BLOCK_GAP;
  }

  return nodes.map(n => {
    const slot = placed.get(String(n.id));
    if (!slot) return n;
    const w = nodeWidth(n);
    const block = Math.floor(slot.col / COLS_PER_BLOCK);
    const x = MARGIN + (slot.col % COLS_PER_BLOCK) * (Q.w + COL_GAP) + (Q.w - w) / 2;
    const y = blockTops[block] + colOffsets.get(slot.col)[slot.row];
    return {
      ...n,
      position: { x: Math.round(x), y: Math.round(y) },
    };
  });
}
